import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, Palmtree, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { useDeleteAbsences } from '@/lib/time-queries'
import type { DayTotal, WorkdayException } from '@/lib/types'
import { useVacationPeriod } from '@/lib/useVacationPeriod'

interface FreeDayBannerProps {
  weekId: string
  personId?: string
  dayIso: string
  dayInfo?: DayTotal
  exception: WorkdayException | null
  editable: boolean
}

export function FreeDayBanner({ weekId, personId, dayIso, dayInfo, exception, editable }: FreeDayBannerProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-hairline bg-fill-2/60 px-4 py-3 text-sm text-ink-soft">
      <span className="flex items-center gap-2">
        <Palmtree className="h-4 w-4 shrink-0 text-ink-faint" />
        <span className="font-medium text-ink">
          {dayInfo?.dayType === 'vacation' ? 'Vakantiedag' : dayInfo?.dayLabel ?? 'Feestdag'}
        </span>
        {exception?.note ? <span className="text-ink-faint">{exception.note}</span> : null}
      </span>
      {editable && dayInfo?.dayType === 'vacation' ? (
        <VacationActions weekId={weekId} personId={personId} dayIso={dayIso} exception={exception} />
      ) : null}
    </div>
  )
}

interface VacationActionsProps {
  weekId: string
  personId?: string
  dayIso: string
  exception: WorkdayException | null
}

/**
 * Een vakantieperiode bestaat uit losse dag-uitzonderingen. Wie midden in zo'n periode
 * staat wil die meestal in een keer opruimen, dus bieden we naast de dag ook de hele
 * aaneengesloten reeks aan, met het aantal dagen erbij.
 */
function VacationActions({ weekId, personId, dayIso, exception }: VacationActionsProps) {
  const period = useVacationPeriod(weekId, personId, dayIso, true)
  const deleteAbsences = useDeleteAbsences(weekId)
  const [open, setOpen] = useState(false)

  if (!period.label) {
    return (
      <button
        type="button"
        onClick={() => exception && deleteAbsences.mutate([exception.id])}
        disabled={!exception || deleteAbsences.isPending || period.loading}
        className="btn-secondary h-8 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <RotateCcw className="h-4 w-4" />
        Vakantie verwijderen
      </button>
    )
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="btn-secondary h-8" disabled={deleteAbsences.isPending}>
          <RotateCcw className="h-4 w-4" />
          Vakantie verwijderen
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-40 w-64 rounded-card border border-hairline bg-surface p-1.5 shadow-pop animate-fade-in"
        >
          <button
            type="button"
            disabled={!exception}
            onClick={() => {
              setOpen(false)
              if (exception) deleteAbsences.mutate([exception.id])
            }}
            className="block w-full rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-fill-2 disabled:opacity-50"
          >
            Alleen deze dag
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              deleteAbsences.mutate(period.ids)
            }}
            className="block w-full rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-fill-2"
          >
            Hele periode
            <span className="mt-0.5 block text-xs text-ink-faint">{`${period.label} · ${period.dates.length} dagen`}</span>
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
